// DMS Project -> "App logic & incident" tab.
// Static findings from the 4 Nov 2026 investigation (read-only SSM review of DMS-PGOC-Web-1/2 and Core-1/2).
import React from 'react';

const K = {
  edge: { s: '#378ADD', f: '#E6F1FB' },
  php: { s: '#7F77DD', f: '#EEEDFE' },
  lib: { s: '#1D9E75', f: '#E1F5EE' },
  job: { s: '#BA7517', f: '#FAEEDA' },
  data: { s: '#D85A30', f: '#FAECE7' },
  risk: '#E24B4A',
};

function N({ x, y, w = 180, h = 50, k, t, s, risk }) {
  const c = K[k];
  return (
    <g>
      <rect x={x} y={y} width={w} height={h} rx="8" fill={c.f} stroke={risk ? K.risk : c.s} strokeWidth={risk ? 2 : 1.2} strokeDasharray={risk ? '5 3' : undefined} />
      <text x={x + w / 2} y={y + 21} textAnchor="middle" fontSize="12.5" fontWeight="600" fill="#1f2937">{t}</text>
      {s && <text x={x + w / 2} y={y + 38} textAnchor="middle" fontSize="10.5" fill="#4b5563">{s}</text>}
    </g>
  );
}

function LogicDiagram() {
  const A = (d, dashed) => <path d={d} fill="none" stroke="#888780" strokeWidth="1.4" markerEnd="url(#dlar)" strokeDasharray={dashed ? '4 3' : undefined} />;
  return (
    <svg viewBox="0 0 960 700" className="w-full" style={{ minWidth: 760 }}>
      <defs><marker id="dlar" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="7" markerHeight="7" orient="auto-start-reverse"><path d="M0,0L10,5L0,10z" fill="#888780" /></marker></defs>

      <text x="20" y="22" fontSize="11" fontWeight="600" fill="#6b7280">1. ENTRY</text>
      <N x={20} y={32} w={200} k="edge" t="Browser / mobile app" s="dms.pgocid.com" />
      <N x={260} y={32} w={210} k="edge" t="Internal ALB 10.40.1.22 / .44" s="2 AZ nodes · sticky 24 h · static health" risk />
      <N x={510} y={32} w={210} k="edge" t="nginx Web-1 .93 / Web-2 .74" s="rewrite → index.php · fastcgi 300 s" />
      <N x={760} y={32} w={180} k="edge" t="/gearman/web UI" s="GearmanManager · no login" risk />
      {A('M220 57H258')}{A('M470 57H508')}{A('M720 57H758', true)}

      <text x="20" y="128" fontSize="11" fontWeight="600" fill="#6b7280">2. PHP-FPM 7.4 · CodeIgniter 3 (CIT framework, core partly ionCube-encoded)</text>
      <N x={510} y={138} w={210} k="php" t="PHP-FPM pool www" s="25 workers · kill after 300 s" />
      {A('M615 82V136')}
      <N x={20} y={210} w={210} k="php" t="Session (CI files driver)" s="/var/lib/php/sessions on NFS (Web-1)" risk />
      <N x={260} y={210} w={210} k="php" t="front module" s="user/Doc_manager · record · content" />
      <N x={510} y={210} w={210} k="php" t="webservice module" s="rest · user · mobile · dashboard" />
      <N x={750} y={210} w={190} k="php" t="admin module" s="users · master · tools" />
      {A('M560 188L365 208')}{A('M615 188V208')}{A('M670 188L845 208')}
      {A('M258 235H232')}

      <text x="20" y="300" fontSize="11" fontWeight="600" fill="#6b7280">3. LIBRARIES (application/libraries)</text>
      <N x={20} y={310} w={200} k="lib" t="Devext_manager" s="actionGetDirContents (recursive)" risk />
      <N x={240} y={310} w={200} k="lib" t="Openkm_manager" s="REST → ALB .22/.44 → Core" />
      <N x={460} y={310} w={150} k="lib" t="Search_manager" s="OpenSearch" />
      <N x={630} y={310} w={140} k="lib" t="Gearmand / Manager" s="doBackground jobs" />
      <N x={790} y={310} w={150} k="lib" t="Others" s="Docusign (demo) · Rag · Camunda" />
      {A('M365 260L120 308')}{A('M615 260L120 308')}{A('M365 260L340 308')}{A('M615 260L535 308')}{A('M365 260L700 308')}
      {A('M220 335H238')}

      <text x="20" y="400" fontSize="11" fontWeight="600" fill="#6b7280">4. BACKGROUND JOBS (Gearman)</text>
      <N x={630} y={410} w={140} k="job" t="gearmand :4730" s="in-memory queue" risk />
      {A('M700 360V408')}
      <N x={380} y={410} w={220} k="job" t="Worker.php start*Worker()" s="CLI loop · started by hand (no service)" risk />
      {A('M628 435H602')}
      <N x={380} y={490} w={220} k="job" t="execute*Worker()" s="exec php index.php job <x> process" />
      {A('M490 460V488')}
      <N x={630} y={490} w={310} k="job" t="9 job types" s="DocumentSync · Extraction · Indexing · Share · Activity · DownloadFolder · DataImport · Dataset · StorageStats" />
      <N x={120} y={490} w={220} k="job" t="updateTaskStatus()" s="Inprocess → Executed (task table)" />
      {A('M378 515H342')}

      <text x="20" y="580" fontSize="11" fontWeight="600" fill="#6b7280">5. DATA &amp; BACKEND SERVICES</text>
      <N x={20} y={590} w={180} k="data" t="RDS MySQL" s="dms_web · dms_okm" />
      <N x={220} y={590} w={230} k="data" t="OpenKM CE 6.3.12 · Core .72 / .82" s="coredms.pgocid.com via ALB .22/.44" risk />
      <N x={470} y={590} w={150} k="data" t="S3 (s3fs)" s="OpenKM datastore" />
      <N x={640} y={590} w={140} k="data" t="OpenSearch" s="dms-pgoc" />
      <N x={800} y={590} w={140} k="data" t="OnlyOffice · socket" s="Redis per Core node" />
      {A('M340 360V588')}{A('M535 360L705 588')}{A('M120 540V588')}{A('M450 615H468')}
      <text x="20" y="680" fontSize="10.5" fill="#6b7280">Red dashed border = risk point found on 4 Nov 2026 (see table below). Memcached runs locally on each web node.</text>
    </svg>
  );
}

const L = {
  en: {
    diagTitle: 'Application logic flow',
    diagNote: 'Rockeye DMS code path as found on 4 Nov 2026 (read-only review of DMS-PGOC-Web-1/2 and Core-1/2). Red dashed border = risk point.',
    stepsTitle: 'How a request is processed',
    risksTitle: 'Risks to watch',
    risksNote: 'Ordered by impact. Items for the vendor need Rockeye to change code or configuration.',
    evTitle: 'Hang evidence (13 Oct – 4 Nov 2026)',
    evNote: 'A node is counted as hung when its 5-minute CPU stays ≥ 20 % for ≥ 15 minutes (one PHP-FPM worker spinning ≈ 25 % of 4 vCPU; normal below 2 %). Source: CloudWatch + CloudTrail, account Indomas.',
    kpi: ['Total hang time', 'Episodes', 'Longest', 'ALB 5xx during hangs'],
    epHead: ['#', 'Node', 'Start (WIB)', 'End (WIB)', 'Hours', '5xx', 'Ended by'],
    riskHead: ['Severity', 'Risk', 'What happens', 'Fix'],
    doneTitle: 'Work done by IT Indomas',
    doneNote: 'All changes made through SSM Run Command and recorded in CloudTrail.',
    doneHead: ['When (WIB)', 'Action'],
    open: 'Still open (vendor): session store off NFS, PHP health check for the ALB, supervised Gearman workers, Gearman on Web-1 reachable from Web-2, requeue of the jobs dropped on 4 Nov.',
    fix: 'Fix',
    sev: { High: 'High', Medium: 'Medium', Low: 'Low' },
    steps: [
      ['Web request', 'Browser → ALB (sticky 24 h) → nginx. Every non-static path is rewritten to index.php; /public/images goes through csshandler.php, so even images use a PHP worker.'],
      ['Controller', 'CodeIgniter routes to the front, webservice or admin module. The session file is opened and LOCKED for the whole request (files driver on NFS from Web-1).'],
      ['Document operations', 'Controllers call Openkm_manager (REST to OpenKM on Core). Folder sync calls Devext_manager::actionGetDirContents recursively inside the web request — large folders (e.g. shared/MR) keep one worker busy for a long time.'],
      ['Background job', 'Heavy work (text extraction, sync, indexing, share, zip download) is queued with Gearman doBackground to the gearmand on the same host, then the request returns.'],
      ['Worker', 'Worker.php start*Worker() runs as a long-lived PHP CLI loop. For each job it runs a second PHP process and writes the result with updateTaskStatus(). Workers must be started by hand — no systemd/supervisor unit.'],
      ['Storage & search', 'Files live in OpenKM (datastore on S3 via s3fs); metadata in RDS (dms_web, dms_okm); content search via OpenSearch.'],
    ],
    risks: [
      ['High', 'Session lock on NFS', 'One stuck request holds the user session lock; other requests queue behind it and fill the PHP pool → nginx 504. Direct cause of the hangs.', 'Move CI sessions to memcached/Redis.'],
      ['High', 'Gearman workers not supervised', 'Workers die on every restart/reboot; the queue is in memory only. On 4 Nov ~641 queued jobs on Web-2 were dropped when gearmand there was stopped.', 'systemd units for workers; persistent queue or requeue from the task table.'],
      ['High', 'Gearman on Web-1 listens on localhost', 'Since 4 Nov 18:55 Web-2 points to 10.40.1.93:4730, which is not reachable → Web-2 cannot queue jobs.', 'Vendor: listen on the private IP + SG rule for port 4730.'],
      ['High', 'Web-1 is a single point of failure', 'Web-2 mounts sessions and uploads from Web-1 over NFS.', 'Shared storage (EFS/S3) and an external session store.'],
      ['Medium', 'Static ALB health check', '/tg-status.html never touches PHP, so a node with a full pool stays "healthy".', 'Health check to a small PHP endpoint.'],
      ['Medium', 'Recursive folder sync in web request', 'actionGetDirContents walks whole folder trees synchronously.', 'Move to a background job; paginate.'],
      ['Medium', 'OpenKM CE on two Core nodes', 'CE is not cluster-aware; index/cache may diverge between nodes.', 'Confirm active/passive with the vendor.'],
      ['Low', 'Security & versions', '/gearman/web open without login; DocuSign demo endpoint with key in config.php; cookies not secure/httponly; admin CSRF off; PHP 7.4 and OpenKM 6.3 are end-of-life.', 'Restrict the UI, move secrets, plan upgrades.'],
    ],
    ended: ['Cleared by itself', 'Cleared by itself (no action in CloudTrail)', 'SSM command by IT, 2 Nov 11:06', 'php-fpm restart by IT, 16:42', 'php-fpm restart by IT, 16:48', 'php-fpm restart by IT, 18:34'],
    done: [
      ['4 Nov 14:50', 'Read-only investigation via SSM: PHP-FPM settings, nginx and FPM logs on both web nodes.'],
      ['4 Nov 18:34', 'Restarted php7.4-fpm on Web-2 (stuck worker holding a session lock; 4 other workers waiting on it).'],
      ['4 Nov 18:38', 'Both web nodes: request_terminate_timeout 300 s, request_slowlog_timeout 10 s + slowlog, max_execution_time 0 → 300, pm dynamic. Backup *.bak-20261104-113845.'],
      ['4 Nov 19:02', 'Both web nodes: pm.max_children 5 → 25 (graceful reload; vendor Gearman workers untouched). Backup *.bak-20261104-120222.'],
      ['4 Nov', 'Full read-only review of the application code, config and Core servers (this tab). Evidence page "DMS Web Hang Evidence" built from CloudWatch + CloudTrail.'],
    ],
  },
  id: {
    diagTitle: 'Alur logika aplikasi',
    diagNote: 'Jalur kode Rockeye DMS sesuai temuan 4 Nov 2026 (review read-only DMS-PGOC-Web-1/2 dan Core-1/2). Garis merah putus-putus = titik rawan.',
    stepsTitle: 'Cara request diproses',
    risksTitle: 'Risiko yang perlu diantisipasi',
    risksNote: 'Diurutkan berdasarkan dampak. Butir untuk vendor memerlukan perubahan kode atau konfigurasi oleh Rockeye.',
    evTitle: 'Bukti hang (13 Okt – 4 Nov 2026)',
    evNote: 'Node dianggap hang bila CPU rata-rata 5 menit tertahan ≥ 20 % selama ≥ 15 menit (satu worker PHP-FPM berputar ≈ 25 % dari 4 vCPU; normalnya di bawah 2 %). Sumber: CloudWatch + CloudTrail, akun Indomas.',
    kpi: ['Total durasi hang', 'Jumlah kejadian', 'Terlama', 'Error 5xx ALB saat hang'],
    epHead: ['#', 'Node', 'Mulai (WIB)', 'Selesai (WIB)', 'Jam', '5xx', 'Berakhir karena'],
    riskHead: ['Tingkat', 'Risiko', 'Yang terjadi', 'Perbaikan'],
    doneTitle: 'Pekerjaan IT Indomas',
    doneNote: 'Semua perubahan dilakukan lewat SSM Run Command dan tercatat di CloudTrail.',
    doneHead: ['Waktu (WIB)', 'Tindakan'],
    open: 'Masih terbuka (vendor): session dipindah dari NFS, health check ALB ke endpoint PHP, worker Gearman sebagai service, Gearman Web-1 bisa diakses dari Web-2, requeue job yang hilang pada 4 Nov.',
    fix: 'Perbaikan',
    sev: { High: 'Tinggi', Medium: 'Sedang', Low: 'Rendah' },
    steps: [
      ['Request web', 'Browser → ALB (sticky 24 jam) → nginx. Semua path non-statis diarahkan ke index.php; /public/images lewat csshandler.php, sehingga gambar pun memakai worker PHP.'],
      ['Controller', 'CodeIgniter meneruskan ke modul front, webservice, atau admin. File session dibuka dan DIKUNCI selama request berjalan (driver files di NFS milik Web-1).'],
      ['Operasi dokumen', 'Controller memanggil Openkm_manager (REST ke OpenKM di Core). Sinkronisasi folder memanggil Devext_manager::actionGetDirContents secara rekursif di dalam request web — folder besar (mis. shared/MR) menahan satu worker lama sekali.'],
      ['Job background', 'Pekerjaan berat (ekstraksi teks, sync, indexing, share, download zip) dimasukkan ke antrean Gearman (doBackground) di host yang sama, lalu request selesai.'],
      ['Worker', 'Worker.php start*Worker() berjalan sebagai loop PHP CLI jangka panjang. Tiap job menjalankan proses PHP kedua dan mencatat hasil dengan updateTaskStatus(). Worker harus dinyalakan manual — tidak ada unit systemd/supervisor.'],
      ['Penyimpanan & pencarian', 'File disimpan di OpenKM (datastore di S3 via s3fs); metadata di RDS (dms_web, dms_okm); pencarian isi lewat OpenSearch.'],
    ],
    risks: [
      ['High', 'Lock session di NFS', 'Satu request macet memegang lock session user; request lain antre dan memenuhi pool PHP → nginx 504. Penyebab langsung hang.', 'Pindahkan session CI ke memcached/Redis.'],
      ['High', 'Worker Gearman tidak dijaga service', 'Worker mati setiap restart/reboot; antrean hanya di memori. Pada 4 Nov sekitar 641 job di Web-2 hilang saat gearmand di sana dihentikan.', 'Unit systemd untuk worker; antrean persisten atau requeue dari tabel task.'],
      ['High', 'Gearman Web-1 hanya listen di localhost', 'Sejak 4 Nov 18:55 Web-2 mengarah ke 10.40.1.93:4730 yang tidak bisa dijangkau → Web-2 tidak bisa membuat job.', 'Vendor: listen di IP private + aturan SG port 4730.'],
      ['High', 'Web-1 titik gagal tunggal', 'Web-2 me-mount session dan upload dari Web-1 via NFS.', 'Storage bersama (EFS/S3) dan session store eksternal.'],
      ['Medium', 'Health check ALB statis', '/tg-status.html tidak menyentuh PHP, sehingga node yang pool-nya penuh tetap dianggap "sehat".', 'Health check ke endpoint PHP kecil.'],
      ['Medium', 'Sync folder rekursif di request web', 'actionGetDirContents menelusuri seluruh pohon folder secara sinkron.', 'Pindahkan ke job background; pakai paginasi.'],
      ['Medium', 'OpenKM CE di dua node Core', 'Versi CE tidak mendukung cluster; index/cache bisa tidak sinkron antar node.', 'Konfirmasi setup active/passive ke vendor.'],
      ['Low', 'Keamanan & versi', '/gearman/web terbuka tanpa login; DocuSign memakai endpoint demo dengan key di config.php; cookie tidak secure/httponly; CSRF admin mati; PHP 7.4 dan OpenKM 6.3 sudah end-of-life.', 'Batasi UI, pindahkan secret, rencanakan upgrade.'],
    ],
    ended: ['Pulih sendiri', 'Pulih sendiri (tidak ada aksi di CloudTrail)', 'SSM command oleh IT, 2 Nov 11:06', 'restart php-fpm oleh IT, 16:42', 'restart php-fpm oleh IT, 16:48', 'restart php-fpm oleh IT, 18:34'],
    done: [
      ['4 Nov 14:50', 'Investigasi read-only via SSM: setting PHP-FPM, log nginx dan FPM di kedua node web.'],
      ['4 Nov 18:34', 'Restart php7.4-fpm di Web-2 (worker macet memegang lock session; 4 worker lain menunggu).'],
      ['4 Nov 18:38', 'Kedua node web: request_terminate_timeout 300 dtk, request_slowlog_timeout 10 dtk + slowlog, max_execution_time 0 → 300, pm dynamic. Backup *.bak-20261104-113845.'],
      ['4 Nov 19:02', 'Kedua node web: pm.max_children 5 → 25 (reload graceful; worker Gearman vendor tidak tersentuh). Backup *.bak-20261104-120222.'],
      ['4 Nov', 'Review read-only lengkap atas kode aplikasi, config, dan server Core (tab ini). Halaman bukti "DMS Web Hang Evidence" dari CloudWatch + CloudTrail.'],
    ],
  },
};

const EPISODES = [
  ['E1', 'Web-2', '22 Oct 14:40', '22 Oct 15:10', '0.5', '0'],
  ['E2', 'Web-2', '22 Oct 17:50', '24 Oct 13:35', '43.8', '177'],
  ['E3', 'Web-2', '28 Oct 17:30', '2 Nov 11:05', '113.6', '603'],
  ['E4', 'Web-1', '4 Nov 10:35', '4 Nov 16:40', '6.1', '210'],
  ['E5', 'Web-2', '4 Nov 16:15', '4 Nov 16:45', '0.5', '15'],
  ['E6', 'Web-2', '4 Nov 17:45', '4 Nov 18:35', '0.8', '10'],
];

const Card = ({ title, note, children }) => (
  <div className="rounded-xl border border-gray-200 bg-white p-4">
    <div className="text-sm font-semibold text-gray-800">{title}</div>
    {note && <div className="mt-0.5 text-xs text-gray-500">{note}</div>}
    <div className="mt-3">{children}</div>
  </div>
);
const T = ({ head, rows }) => (
  <div className="overflow-x-auto rounded-lg border border-gray-200">
    <table className="w-full text-sm">
      <thead><tr className="bg-gray-50 text-left text-xs text-gray-500">{head.map((h) => <th key={h} className="px-3 py-2">{h}</th>)}</tr></thead>
      <tbody>{rows.map((r, i) => <tr key={i} className="border-t border-gray-100">{r.map((c, j) => <td key={j} className="px-3 py-2 align-top text-gray-700">{c}</td>)}</tr>)}</tbody>
    </table>
  </div>
);
const sev = { High: 'bg-red-50 text-red-700', Medium: 'bg-amber-50 text-amber-700', Low: 'bg-gray-100 text-gray-600' };

export default function DmsLogicTab() {
  const [lang, setLang] = React.useState(() => { try { return localStorage.getItem('dms_logic_lang') || 'en'; } catch (_) { return 'en'; } });
  const pick = (l) => { setLang(l); try { localStorage.setItem('dms_logic_lang', l); } catch (_) {} };
  const t = L[lang];
  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <div className="inline-flex rounded-md bg-gray-100 p-0.5 text-xs">
          {[['en', 'EN'], ['id', 'ID']].map(([k, l]) => (
            <button key={k} onClick={() => pick(k)} className={`rounded px-3 py-1 font-semibold ${lang === k ? 'bg-white text-gray-900 shadow-sm ring-1 ring-gray-200' : 'text-gray-500 hover:text-gray-700'}`}>{l}</button>
          ))}
        </div>
      </div>
      <Card title={t.diagTitle} note={t.diagNote}>
        <div className="overflow-x-auto"><LogicDiagram /></div>
      </Card>
      <Card title={t.stepsTitle}>
        <ol className="space-y-1.5">
          {t.steps.map(([a, d], i) => (
            <li key={a} className="flex gap-2 text-sm">
              <span className="mt-0.5 h-5 w-5 shrink-0 rounded-full bg-indigo-100 text-center text-xs font-semibold leading-5 text-indigo-800">{i + 1}</span>
              <span><b className="text-gray-800">{a}.</b> <span className="text-gray-600">{d}</span></span>
            </li>
          ))}
        </ol>
      </Card>
      <Card title={t.risksTitle} note={t.risksNote}>
        <T head={t.riskHead} rows={t.risks.map(([s, a, b, c]) => [<span className={`rounded px-1.5 py-0.5 text-xs font-medium ${sev[s]}`}>{t.sev[s]}</span>, <b className="text-gray-800">{a}</b>, b, c])} />
      </Card>
      <Card title={t.evTitle} note={t.evNote}>
        <div className="mb-3 grid grid-cols-2 gap-3 sm:grid-cols-4">
          {['165.3 h', '6', '113.6 h', '1,015'].map((v, i) => (
            <div key={i} className="rounded-lg border border-gray-200 p-3"><div className="text-xs text-gray-500">{t.kpi[i]}</div><div className="mt-1 text-xl font-semibold text-gray-900">{v}</div></div>
          ))}
        </div>
        <T head={t.epHead} rows={EPISODES.map((r, i) => [...r, t.ended[i]])} />
      </Card>
      <Card title={t.doneTitle} note={t.doneNote}>
        <T head={t.doneHead} rows={t.done} />
        <div className="mt-2 text-xs text-gray-500">{t.open}</div>
      </Card>
    </div>
  );
}
